/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useState, Fragment, useEffect, useRef } from 'react'
import styled, { useTheme } from 'styled-components'
import { ReactEditor, useReadOnly, useFocused, useSelected, useSlate } from 'vendor/slate-react'
import { Transforms } from 'vendor/slate'
import debounce from 'lodash-es/debounce'
import { Ratio, Button, ButtonGroup, FormControl, FormGroup } from 'react-bootstrap'
import { ELEMENT_TYPES } from 'cg/components/documents/editor/slate/types'
import { extractHostname } from 'cg/components/documents/helpers'
import psl from 'psl'
import ReactPlayer from 'react-player'
import { getTheme } from 'Shared/theme'
import Gist from 'react-gist'

let isIframeResizing = null

const Frame = ({ src }) => (
  <IFRAME
    loading="lazy"
    allowFullScreen
    sandbox="allow-scripts allow-popups allow-top-navigation-by-user-activation allow-forms allow-same-origin"
    src={src}
  />
)

const EmbedError = () => (
  <EmbedErrorMessage>
    <i className="fad fa-exclamation-triangle" />
  </EmbedErrorMessage>
)

const Embed = ({ type, src }) => {
  if (!src) return null
  const hostname = psl.get(extractHostname(src))
  if (type === ELEMENT_TYPES.pdf) return <IFRAME loading="lazy" src={src} />

  switch (hostname) {
    case 'loom.com':
      return <Frame src={src.replace('/share/', '/embed/')} />
    case 'figma.com':
      return <Frame src={`https://www.figma.com/embed?embed_host=astra&url=${src}`} />
    case 'github.com':
      const steps = src.split('gist.github.com')
      if (!steps || steps.length !== 2) return <EmbedError />
      const matches = steps[1].split('#')
      const gistPath = matches[0].replace(/^\/+/, '')
      const gistId = gistPath ? gistPath.split('/')[1] : null
      return (
        <GitHubGistContainer>
          <Gist id={gistId} />
        </GitHubGistContainer>
      )
    default:
      if (ReactPlayer.canPlay(src)) {
        return (
          <ReactPlayer url={src} width="100%" height="100%" style={{ width: '100%', height: '100%' }} controls fallback={<p />} />
        )
      }
      return <Frame src={src} />
  }
}

const EmbedWrapper = ({ attributes, children, element, editor }) => {
  const readOnly = useReadOnly()
  const { src, embed_code, size, aspect, placeholder, type, responsiveEmbedDisabled, aspectDisabled } = element
  const [iframeSize, setIframeSize] = useState(size)
  const [srcInputOpen, setSrcInputOpen] = useState(false)
  const [srcValue, setSrcValue] = useState(src)
  const path = ReactEditor.findPath(editor, element)
  const theme = useTheme()
  const filteredAspect = aspect.replace('by', 'x')
  return (
    <IframeControlsWrapper {...attributes}>
      {!readOnly && (
        <IframeSizeSliderWrapper contentEditable={false}>
          <IframeSizeSlider
            type="range"
            min="10"
            max="100"
            value={iframeSize || 100}
            onChange={e => {
              const { value } = e.target
              if (value.includes('application/x-slate-clipboard:')) {
                return
              }
              setIframeSize(value)
              if (isIframeResizing) isIframeResizing.cancel()
              isIframeResizing = debounce(value => {
                Transforms.setNodes(editor, { size: value }, { at: path })
              }, 300)
              isIframeResizing(value)
            }}
          />
        </IframeSizeSliderWrapper>
      )}
      <IframeContentWrapper contentEditable={false}>
        {responsiveEmbedDisabled ? (
          <div
            style={{
              width: `${size}%`,
              background: src && src.length > 0 ? getTheme(theme.id).bgLightest : getTheme(theme.id).hoverBgLightestInverse,
              minHeight: !src || src.length === 0 ? '150px' : 'auto'
            }}
          >
            {embed_code ? <span dangerouslySetInnerHTML={{ __html: src }} /> : <Embed src={src} type={type} />}
          </div>
        ) : (
          <Ratio
            aspectRatio={filteredAspect}
            style={{
              width: `${size}%`,
              background: src && src.length > 0 ? getTheme(theme.id).bgLightest : getTheme(theme.id).hoverBgLightestInverse
            }}
          >
            {embed_code ? <EmbedCode dangerouslySetInnerHTML={{ __html: src }} /> : <Embed src={src} type={type} />}
          </Ratio>
        )}

        {!readOnly && (
          <IframControlsAndSrcWrapper>
            <IframeControls>
              <EmbedControlButton
                variant="secondary"
                size="sm"
                active={srcInputOpen}
                onClick={() => {
                  setSrcInputOpen(!srcInputOpen)
                }}
              >
                {embed_code === true ? <i className="fad fa-code" /> : <i className="fad fa-link" />}
              </EmbedControlButton>
              {!aspectDisabled && (
                <ButtonGroup>
                  <EmbedControlButton
                    variant="secondary"
                    size="sm"
                    active={filteredAspect === '1x1'}
                    onClick={() => {
                      Transforms.setNodes(editor, { aspect: '1x1' }, { at: path })
                    }}
                  >
                    1:1
                  </EmbedControlButton>
                  <EmbedControlButton
                    variant="secondary"
                    size="sm"
                    active={filteredAspect === '4x3'}
                    onClick={() => {
                      Transforms.setNodes(editor, { aspect: '4x3' }, { at: path })
                    }}
                  >
                    4:3
                  </EmbedControlButton>
                  <EmbedControlButton
                    variant="secondary"
                    size="sm"
                    active={filteredAspect === '16x9'}
                    onClick={() => {
                      Transforms.setNodes(editor, { aspect: '16x9' }, { at: path })
                    }}
                  >
                    16:9
                  </EmbedControlButton>
                  <EmbedControlButton
                    variant="secondary"
                    size="sm"
                    active={filteredAspect === '21x9'}
                    onClick={() => {
                      Transforms.setNodes(editor, { aspect: '21x9' }, { at: path })
                    }}
                  >
                    21:9
                  </EmbedControlButton>
                </ButtonGroup>
              )}
            </IframeControls>
            {srcInputOpen && (
              <IframeSrc>
                <FormGroup className="mb-3">
                  <FormControl
                    size="sm"
                    onClick={e => e.stopPropagation()}
                    style={{ height: embed_code === true ? '70px' : 'inherit' }}
                    as={embed_code === true ? 'textarea' : 'input'}
                    placeholder={placeholder}
                    value={srcValue || ''}
                    onChange={e => {
                      const { value } = e.target
                      if (value.includes('application/x-slate-clipboard:')) {
                        return
                      }
                      setSrcValue(value)
                    }}
                  />
                </FormGroup>
                <Button
                  size="sm"
                  variant={src !== srcValue && srcValue && srcValue.length > 0 ? 'primary' : 'secondary'}
                  disabled={src === srcValue || !srcValue}
                  onClick={() => {
                    Transforms.setNodes(editor, { src: srcValue.replace('http://', 'https://') }, { at: path })
                  }}
                >
                  Save
                </Button>
              </IframeSrc>
            )}
          </IframControlsAndSrcWrapper>
        )}
      </IframeContentWrapper>
      {children}
    </IframeControlsWrapper>
  )
}

export default EmbedWrapper

const IframeControlsWrapper = styled.div``
const IframeSizeSliderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
  align-items: center;
`

const IframeSizeSlider = styled.input`
  -webkit-appearance: none;
  margin-top:10px;
  appearance: none;
  width:50%;
  margin-right:10px;
  outline: none;
  background: ${props => props.theme.progressBarBg};
  outline: none;
  opacity: 0.7;
  -webkit-transition: .2s;
  transition: opacity .2s;
  height:7px;
  border-radius:6px;
  align-items:center;
  :hover {
    opacity: 1;
  }
  ::-webkit-slider-thumb{
    -webkit-appearance: none;
    appearance: none;
    width: 15px;
    height: 15px;
    background: ${props => props.theme.primary};
    border-radius:50%;
    cursor: pointer;
  }
  ::-moz-range-thumb{
    width: 16px;
    height: 16px;
    background: ${props => props.theme.primary};
    border-radius:50%;
    cursor: pointer;
  }
}
`

const IframControlsAndSrcWrapper = styled.div`
  margin: 15px 0px 15px 0px;
  width: 100%;
`
const IframeControls = styled.div`
  justify-content: space-between;
  width: 100%;
  display: flex;
`

const IframeContentWrapper = styled.div`
  width: 100%;
  display: flex;
  -webkit-box-pack: center;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  margin-bottom: 15px;
`

const IframeSrc = styled.div`
  font-size: 16px;
  color: ${props => props.theme.textGreyTintedLighter};
  margin-top: 15px;
  margin-bottom: 10px;
  width: 100%;
  text-align: right;
  input::placeholder {
    color: ${props => props.theme.textGreyTintedLighter};
  }
`

const EmbedControlButton = styled(Button)`
  color: ${props => props.theme.textGreyTinted}; !important;
  font-size: 11px;
  font-weight: bold;
`

const GitHubGistContainer = styled.div`
  line-height: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  &::before {
    padding-top: 0px !important;
  }
`

const EmbedErrorMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 150px;
  background: ${props => props.theme.hoverBgLightestInverse};
  color: red;
  font-size: 30px;
`

const IFRAME = styled.iframe``
const EmbedCode = styled.span`
  iframe {
    width: 100%;
    height: 100%;
  }
`
